import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import React, { useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import html2canvas from "html2canvas";

const CHIP_SRC = "/assets/cards/captec-card-chip.png";

const CARD_CHIP_BOX = {
  top: "92px",
  left: "30px",
  width: "76px",
  height: "58px",
};

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const colors = [
  "#0A2558",
  "#00BCD4",
  "#03A9F4",
  "#1B5E20",
  "#4CAF50",
  "#B71C1C",
  "#E53935",
  "#FF9800",
  "#795548",
  "#222222",
  "#607D8B",
  "#ffffff",
];

const patterns = [
  { id: "ondas", label: "Ondas", preview: "preview-ondas" },
  { id: "hexagonos", label: "Geométrico", preview: "preview-hexagonos" },
  { id: "puntos", label: "Puntos", preview: "preview-puntos" },
  { id: "lineas", label: "Líneas", preview: "preview-lineas" },
  { id: "circuito", label: "Circuito", preview: "preview-circuito" },
  { id: "none", label: "Sin patrón", preview: "preview-none" },
];

const getBrightness = (hex) => {
  const rgb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!rgb) return 0;

  return (
    0.2126 * parseInt(rgb[1], 16) +
    0.7152 * parseInt(rgb[2], 16) +
    0.0722 * parseInt(rgb[3], 16)
  );
};

const getPatternStyle = (pattern, color) => {
  const isLight = getBrightness(color) > 160;
  const cLine = isLight ? "%23000000" : "%23ffffff";
  const cssColor = isLight ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.5)";
  const base = { opacity: isLight ? 0.05 : 0.08 };

  if (pattern === "none") {
    return {
      ...base,
      backgroundImage: "none",
    };
  }

  if (pattern === "ondas") {
    return {
      ...base,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='180' height='60' viewBox='0 0 180 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 30 Q 45 0, 90 30 T 180 30 M0 45 Q 45 15, 90 45 T 180 45' fill='none' stroke='${cLine}' stroke-width='1.5'/%3E%3C/svg%3E")`,
      backgroundSize: "180px 60px",
    };
  }

  if (pattern === "hexagonos") {
    return {
      ...base,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='24' height='42' viewBox='0 0 24 42' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 1L23 7V20L12 26L1 20V7L12 1Z' fill='none' stroke='${cLine}' stroke-width='1.5'/%3E%3C/svg%3E")`,
      backgroundSize: "40px auto",
    };
  }

  if (pattern === "puntos") {
    return {
      ...base,
      backgroundImage: `radial-gradient(${cssColor} 1.5px, transparent 1.5px)`,
      backgroundSize: "15px 15px",
    };
  }

  if (pattern === "lineas") {
    return {
      ...base,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 0 20 L 20 0 M -10 10 L 10 -10 M 10 30 L 30 10' fill='none' stroke='${cLine}' stroke-width='1.5'/%3E%3C/svg%3E")`,
      backgroundSize: "20px 20px",
    };
  }

  return {
    ...base,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='${cLine}' stroke-width='3'%3E%3Cpath d='M0 40 h 20 l 10 -10 h 20 l 10 10 h 20 M20 40 l 10 10 h 20 l 10 -10' /%3E%3Ccircle cx='50' cy='30' r='5' fill='${cLine}'/%3E%3Ccircle cx='50' cy='50' r='5' fill='${cLine}'/%3E%3C/g%3E%3C/svg%3E")`,
    backgroundSize: "60px 60px",
  };
};

const waitForPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

const waitForImages = async (element) => {
  const images = Array.from(element.querySelectorAll("img"));

  await Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }

          img.onload = resolve;
          img.onerror = resolve;
        })
    )
  );
};

function CardChip() {
  return (
    <div
      className="card-chip-area elemento-top"
      style={{
        position: "absolute",
        top: CARD_CHIP_BOX.top,
        left: CARD_CHIP_BOX.left,
        width: CARD_CHIP_BOX.width,
        height: CARD_CHIP_BOX.height,
        zIndex: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <img
        src={CHIP_SRC}
        alt=""
        aria-hidden="true"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
          filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.25))",
        }}
        onError={(event) => {
          event.currentTarget.style.display = "none";
          const fallback = event.currentTarget.nextElementSibling;

          if (fallback) {
            fallback.style.display = "block";
          }
        }}
      />

      <span
        aria-hidden="true"
        style={{
          display: "none",
          width: "76px",
          height: "56px",
          borderRadius: "12px",
          background:
            "linear-gradient(135deg, rgba(255,214,102,0.95), rgba(176,121,22,0.95))",
          boxShadow:
            "inset 0 0 0 2px rgba(255,255,255,0.22), 0 8px 18px rgba(0,0,0,0.25)",
        }}
      />
    </div>
  );
}

function CardFace({ side, patternStyle, logo, invertLogo, name }) {
  if (side === "back") {
    return (
      <div className="tarjeta-face tarjeta-back">
        <div className="back-shine" />
        <div className="patron-layer" style={patternStyle} />
        <div className="magnetic-strip" />

        <div className="signature-row">
          <div className="signature-panel">AUTHORIZED SIGNATURE</div>
          <div className="cvv-box">123</div>
        </div>

        <div className="back-copy">
          This card is issued for authorized use only. If found, please return to the issuing
          institution.
        </div>

        <div className="back-footer">
          <div className="back-brand-lockup">CAPTEC · CARD PROCESSING TECHNOLOGY</div>
          <div className="back-contactless">SECURE PROCESSING</div>
        </div>
      </div>
    );
  }

  return (
    <div className="tarjeta-face tarjeta-front">
      <div className="brillo-estatico" />
      <div className="patron-layer" style={patternStyle} />

      <div className="logo-usuario-area elemento-top">
        {logo ? (
          <img
            src={logo}
            alt="Logo cargado por el usuario"
            style={{
              display: "block",
              filter: invertLogo ? "invert(1)" : "none",
            }}
          />
        ) : null}
      </div>

      <CardChip />

      <div className="numeros-tarjeta elemento-top">.... .... .... 0000</div>

      <div className="info-inferior elemento-top">
        <div className="nombre-cliente">{name || "NOMBRE EMPRESA"}</div>

        <div className="fecha-tarjeta">
          VÁLIDA HASTA
          <br />
          <span>12/28</span>
        </div>
      </div>

      <div className="marca-tarjeta elemento-top">
        <div className="circulo red" />
        <div className="circulo orange" />
      </div>

      <div className="brillo-dinamico" />
    </div>
  );
}

export default function CardCustomizer() {
  const tarjetaRef = useRef(null);

  const [cardType, setCardType] = useState("DÉBITO");
  const [color, setColor] = useState("#222222");
  const [pattern, setPattern] = useState("ondas");
  const [name, setName] = useState("");
  const [logo, setLogo] = useState("");
  const [invertLogo, setInvertLogo] = useState(false);
  const [rotation, setRotation] = useState({ x: -8, y: -12 });
  const [isExporting, setIsExporting] = useState(false);
  const [isLeadFormOpen, setIsLeadFormOpen] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadError, setLeadError] = useState("");
  const [leadForm, setLeadForm] = useState({
    fullName: "",
    phone: "",
    email: "",
  });

  const dragRef = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    baseX: 0,
    baseY: 0,
  });

  const patternStyle = useMemo(() => getPatternStyle(pattern, color), [pattern, color]);
  const displayName = name.trim().toUpperCase() || "NOMBRE EMPRESA";

  const handleLogo = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = (readerEvent) => {
      setLogo(readerEvent.target?.result || "");
    };

    reader.readAsDataURL(file);
  };

  const updateLight = (event) => {
    const tarjeta = tarjetaRef.current;
    const light = tarjeta?.querySelector(".brillo-dinamico");

    if (!tarjeta || !light) return;

    const rect = tarjeta.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    light.style.opacity = "0.8";
    light.style.background = `radial-gradient(circle at ${x}px ${y}px, rgba(255,255,255,0.42) 0%, transparent 58%)`;
  };

  const onPointerDown = (event) => {
    dragRef.current = {
      dragging: true,
      startX: event.clientX,
      startY: event.clientY,
      baseX: rotation.x,
      baseY: rotation.y,
    };

    tarjetaRef.current?.classList.add("is-dragging");
    tarjetaRef.current?.classList.remove("reset-animacion");
    tarjetaRef.current?.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    updateLight(event);

    if (!dragRef.current.dragging) return;

    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;

    setRotation({
      y: dragRef.current.baseY + deltaX * 0.45,
      x: dragRef.current.baseX - deltaY * 0.45,
    });
  };

  const endDrag = (event) => {
    if (!dragRef.current.dragging) return;

    dragRef.current.dragging = false;
    tarjetaRef.current?.classList.remove("is-dragging");

    try {
      tarjetaRef.current?.releasePointerCapture?.(event.pointerId);
    } catch (_) {
      // Pointer capture may already be released.
    }
  };

  const resetView = () => {
    tarjetaRef.current?.classList.add("reset-animacion");
    setRotation({ x: 0, y: 0 });

    const light = tarjetaRef.current?.querySelector(".brillo-dinamico");

    if (light) {
      light.style.opacity = "0";
    }
  };

  const captureSide = async (side) => {
    const sandbox = document.createElement("div");
    sandbox.className = "captec-home card-export-sandbox";

    const flatCard = document.createElement("div");
    flatCard.className = "card-export-flat";
    flatCard.style.setProperty("--card-bg", color);

    sandbox.appendChild(flatCard);
    document.body.appendChild(sandbox);

    const root = createRoot(flatCard);

    try {
      flushSync(() => {
        root.render(
          <CardFace
            side={side}
            patternStyle={patternStyle}
            logo={logo}
            invertLogo={invertLogo}
            name={displayName}
          />
        );
      });

      await waitForPaint();
      await waitForImages(flatCard);
      await waitForPaint();

      const canvas = await html2canvas(flatCard, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
        logging: false,
        foreignObjectRendering: false,
        removeContainer: false,
        windowWidth: 1000,
        windowHeight: 640,
      });

      return canvas;
    } finally {
      root.unmount();
      sandbox.remove();
    }
  };

  const createPreviewCanvas = async () => {
    const light = tarjetaRef.current?.querySelector(".brillo-dinamico");

    if (light) {
      light.style.opacity = "0";
    }

    const frontCanvas = await captureSide("front");
    const backCanvas = await captureSide("back");

    const gap = 90;
    const labelHeight = 76;
    const padding = 56;
    const outputCanvas = document.createElement("canvas");

    outputCanvas.width = frontCanvas.width + backCanvas.width + gap + padding * 2;
    outputCanvas.height =
      Math.max(frontCanvas.height, backCanvas.height) + labelHeight + padding * 2;

    const ctx = outputCanvas.getContext("2d");

    if (!ctx) {
      throw new Error("No se pudo crear el canvas de exportación.");
    }

    ctx.fillStyle = "#01060e";
    ctx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);

    const gradient = ctx.createRadialGradient(
      outputCanvas.width / 2,
      outputCanvas.height / 2,
      40,
      outputCanvas.width / 2,
      outputCanvas.height / 2,
      outputCanvas.width / 1.2
    );

    gradient.addColorStop(0, "rgba(0, 229, 255, 0.16)");
    gradient.addColorStop(0.45, "rgba(20, 35, 70, 0.18)");
    gradient.addColorStop(1, "rgba(1, 6, 14, 1)");

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);

    const frontX = padding;
    const backX = padding + frontCanvas.width + gap;
    const cardsY = padding + labelHeight;

    ctx.fillStyle = "#eaf7ff";
    ctx.font = "700 34px Poppins, sans-serif";
    ctx.textBaseline = "top";

    ctx.fillText("FRENTE", frontX, padding);
    ctx.fillText("REVERSO", backX, padding);
    ctx.drawImage(frontCanvas, frontX, cardsY);
    ctx.drawImage(backCanvas, backX, cardsY);

    return outputCanvas;
  };

  const triggerPreviewDownload = (previewDataUrl) => {
    const safeName = displayName
      .replace(/[^a-z0-9áéíóúñü\s.-]/gi, "")
      .replace(/\s+/g, "-")
      .toUpperCase();

    const link = document.createElement("a");
    link.download = `Tarjeta-${safeName || "CAPTEC"}-Frente-Reverso.png`;
    link.href = previewDataUrl;
    link.click();
  };

  const submitCardLead = async (previewDataUrl) => {
    const response = await fetch(`${API_BASE_URL}/api/card-design-leads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        full_name: leadForm.fullName.trim(),
        phone: leadForm.phone.trim(),
        email: leadForm.email.trim(),
        card_design: {
          card_type: cardType,
          color,
          pattern,
          displayed_name: displayName,
          has_custom_logo: Boolean(logo),
          invert_logo: invertLogo,
        },
        preview_image_data: previewDataUrl,
      }),
    });

    if (!response.ok) {
      throw new Error(`No se pudo registrar el interesado (${response.status}).`);
    }

    return response.json();
  };

  const openLeadForm = () => {
    setLeadError("");
    setLeadSubmitted(false);
    setIsLeadFormOpen(true);
  };

  const closeLeadForm = () => {
    if (isExporting) return;
    setIsLeadFormOpen(false);
    setLeadError("");
    setLeadSubmitted(false);
  };

  const handleLeadField = (event) => {
    const { name: fieldName, value } = event.target;
    setLeadForm((current) => ({ ...current, [fieldName]: value }));
  };

  const handleLeadSubmit = async (event) => {
    event.preventDefault();

    if (isExporting) return;

    const fullName = leadForm.fullName.trim();
    const phone = leadForm.phone.trim();
    const email = leadForm.email.trim();
    const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (fullName.length < 2 || phone.replace(/\D/g, "").length < 7 || !emailIsValid) {
      setLeadError("Completa nombre, teléfono y correo con datos válidos.");
      return;
    }

    setLeadError("");
    setIsExporting(true);

    try {
      const outputCanvas = await createPreviewCanvas();
      const previewDataUrl = outputCanvas.toDataURL("image/png");

      try {
        await submitCardLead(previewDataUrl);
      } catch (requestError) {
        console.warn("Registro de Card Studio pendiente de conexión con backend:", requestError);
      }

      triggerPreviewDownload(previewDataUrl);
      setLeadSubmitted(true);
    } catch (error) {
      console.error("Error generando vista previa de tarjeta:", error);
      setLeadError("No se pudo generar la imagen de la tarjeta. Intenta nuevamente.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="personalizador-wrapper dark-theme">

      <div className="tarjeta-col">
        <div className="card-stage-copy">
          <span className="stage-kicker">Card Studio</span>
          <h2>Diseña tu tarjeta. Nosotros implementamos todo lo que hay detrás.</h2>
          <p>
            Crea una vista previa y descubre cómo CAPTEC lleva tu tarjeta del diseño a la emisión, certificación, digitalización y procesamiento.
          </p>
        </div>

        <div className="tarjeta-flotante-wrapper">

          <div className="drag-hint">
            <span>Arrastra para girar 360°</span>
          </div>

          <div className="tarjeta-scene">
            <div
              className="tarjeta"
              id="miTarjeta"
              ref={tarjetaRef}
              aria-label="Vista previa interactiva de tarjeta"
              style={{
                "--card-bg": color,
                transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onPointerLeave={() => {
                if (!dragRef.current.dragging) {
                  const light = tarjetaRef.current?.querySelector(".brillo-dinamico");

                  if (light) {
                    light.style.opacity = "0";
                  }
                }
              }}
            >
              <CardFace
                side="front"
                patternStyle={patternStyle}
                logo={logo}
                invertLogo={invertLogo}
                name={displayName}
              />

              <CardFace
                side="back"
                patternStyle={patternStyle}
                logo={logo}
                invertLogo={invertLogo}
                name={displayName}
              />
            </div>
          </div>
        </div>

        <div className="card-meta">
          <div className="texto-tipo-tarjeta">{cardType} · MASTERCARD</div>

          <div className="card-actions-row">
            <button className="btn-mini" type="button" onClick={resetView}>
              Centrar vista
            </button>

            <button
              className="btn-descargar"
              type="button"
              onClick={openLeadForm}
              disabled={isExporting}
            >
              {isExporting ? "Generando diseño..." : "Crear mi tarjeta"}
            </button>
          </div>
        </div>
      </div>

      <div className="controles-col">
        <div className="control-panel-header">
          <span className="stage-kicker">Configurador</span>
          <h3>Personaliza tu tarjeta</h3>
          <p>
            Selecciona tipo, color, patrón y marca para generar una vista previa editable.
          </p>
        </div>

        <section className="control-group">
          <h3 className="titulo-control">
            <span className="num-step">1</span>
            Tipo de tarjeta
          </h3>

          <div className="tipo-grid">
            {["DÉBITO", "CRÉDITO", "PREPAGO"].map((type) => (
              <button
                key={type}
                className={`btn-tipo ${cardType === type ? "activo" : ""}`}
                aria-pressed={cardType === type}
                type="button"
                onClick={() => setCardType(type)}
              >
                {type.charAt(0) + type.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </section>

        <section className="control-group">
          <h3 className="titulo-control">
            <span className="num-step">2</span>
            Color de fondo
          </h3>

          <div className="colores-grid" aria-label="Colores disponibles">
            {colors.map((cardColor) => (
              <button
                key={cardColor}
                type="button"
                className={`btn-color ${color === cardColor ? "activo" : ""}`}
                aria-pressed={color === cardColor}
                style={{ backgroundColor: cardColor }}
                onClick={() => setColor(cardColor)}
                aria-label={`Usar color ${cardColor}`}
              />
            ))}

            <label className="btn-color color-picker-wrapper" aria-label="Elegir color personalizado">
              <input
                type="color"
                value={color}
                onChange={(event) => setColor(event.target.value)}
              />
            </label>
          </div>
        </section>

        <section className="control-group">
          <h3 className="titulo-control">
            <span className="num-step">3</span>
            Diseño / patrón
          </h3>

          <div className="patrones-grid">
            {patterns.map((item) => (
              <button
                key={item.id}
                className={`btn-patron ${pattern === item.id ? "activo" : ""}`}
                aria-pressed={pattern === item.id}
                type="button"
                onClick={() => setPattern(item.id)}
              >
                <div className={`preview-box ${item.preview}`}>
                  {item.id === "none" ? "∅" : ""}
                </div>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="control-group">
          <h3 className="titulo-control">
            <span className="num-step">4</span>
            Logo de tu empresa
          </h3>

          <div className="logo-upload-wrapper">
            <div className="upload-box-container">
              <label htmlFor="inputLogo" className="upload-box">
                <span className="upload-icon">↥</span>
                <strong>Subir logo</strong>
                <small>PNG recomendado</small>
              </label>

              <input
                type="file"
                id="inputLogo"
                accept="image/*"
                onChange={handleLogo}
              />
            </div>

            <div className="logo-actions-col">
              <label className="toggle-control">
                <input
                  type="checkbox"
                  checked={invertLogo}
                  onChange={(event) => setInvertLogo(event.target.checked)}
                />

                <span className="toggle-track">
                  <span className="toggle-thumb" />
                </span>

                <span className="toggle-text">Invertir logo</span>
              </label>

              <button
                className="btn-accion btn-quitar"
                type="button"
                onClick={() => setLogo("")}
              >
                Quitar logo
              </button>
            </div>
          </div>
        </section>

        <section className="control-group">
          <h3 className="titulo-control">
            <span className="num-step">5</span>
            Nombre en tarjeta
          </h3>

          <input
            type="text"
            className="input-nombre"
            aria-label="Nombre en tarjeta"
            placeholder="Nombre Empresa"
            maxLength={20}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </section>
      </div>

      <Dialog open={isLeadFormOpen} onOpenChange={(open) => { if (!open) closeLeadForm(); }}>
        <DialogContent className="card-lead-dialog" onCloseAutoFocus={(event) => {
          event.preventDefault();
          document.querySelector(".card-actions-row .btn-descargar")?.focus({ preventScroll: true });
        }}>
          <DialogDescription className="sr-only">Datos de contacto para tu proyecto de emisión.</DialogDescription>
            {leadSubmitted ? (
              <div className="card-lead-success" role="status">
                <span className="card-lead-success-icon">✓</span>
                <p className="stage-kicker">Diseño generado</p>
                <DialogTitle className="card-lead-title">Tu vista previa ya se descargó</DialogTitle>
                <p>La solicitud quedó preparada para el seguimiento comercial de Captec.</p>
                <button type="button" className="btn-descargar" onClick={closeLeadForm}>
                  Cerrar
                </button>
              </div>
            ) : (
              <form className="card-lead-form" onSubmit={handleLeadSubmit}>
                <p className="stage-kicker">Recibe tu propuesta</p>
                <DialogTitle className="card-lead-title">Hablemos de tu proyecto de emisión</DialogTitle>
                <p className="card-lead-intro">
                  Completa tus datos. Al continuar se descargará la vista previa que acabas de crear.
                </p>

                <label>
                  <span>Nombre</span>
                  <input
                    type="text"
                    name="fullName"
                    autoComplete="name"
                    value={leadForm.fullName}
                    onChange={handleLeadField}
                    placeholder="Nombre y apellido"
                    required
                  />
                </label>

                <label>
                  <span>Número de teléfono</span>
                  <input
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    value={leadForm.phone}
                    onChange={handleLeadField}
                    placeholder="Número de contacto"
                    required
                  />
                </label>

                <label>
                  <span>Correo electrónico</span>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={leadForm.email}
                    onChange={handleLeadField}
                    placeholder="correo@empresa.com"
                    required
                  />
                </label>

                {leadError ? <p className="card-lead-error" role="alert">{leadError}</p> : null}

                <button className="btn-descargar card-lead-submit" type="submit" disabled={isExporting}>
                  {isExporting ? "Preparando descarga..." : "Aceptar y descargar"}
                </button>

                <small>
                  El envío automático del diseño por correo queda preparado para la próxima configuración del proveedor.
                </small>
              </form>
            )}
        </DialogContent>
      </Dialog>
    </div>
  );
}